import axios from "../../api/axiosInstance";
import { STATUS, getProjectId, extractErrorInfo } from "./sharedfunction";

export async function getModules() {
  try {
    const projectId = getProjectId();
    
    if (!projectId) {
      return {
        status: STATUS.ERROR,
        error: "Project ID is required",
      };
    }

    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/${projectId}/modules/get`,
      {
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || [],
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error fetching modules:", error);
    return extractErrorInfo(error);
  }
}

export async function createModule({ name, level, parentId, context } = {}) {
  try {
    const projectId = getProjectId();
    
    if (!projectId) {
      return {
        status: STATUS.ERROR,
        error: "Project ID is required",
      };
    }

    if (!name || name.trim().length === 0) {
      return {
        status: STATUS.ERROR,
        error: "Module name is required",
      };
    }

    if (!level || level < 1 || level > 3) {
      return {
        status: STATUS.ERROR,
        error: "Level must be between 1 and 3",
      };
    }

    const payload = {
      name,
      level,
      ...(parentId && { parentId }),
      ...(context !== undefined && { context }),
    };

    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/${projectId}/modules/create`,
      payload,
      {
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error creating module:", error);
    return extractErrorInfo(error);
  }
}

export async function updateModule({ id, name, context } = {}) {
  try {
    const projectId = getProjectId();
    
    if (!projectId) {
      return {
        status: STATUS.ERROR,
        error: "Project ID is required",
      };
    }

    if (!id) {
      return {
        status: STATUS.ERROR,
        error: "Module ID is required",
      };
    }

    const payload = {};
    if (name !== undefined) {
      payload.name = name;
    }
    if (context !== undefined) {
      payload.context = context;
    }

    if (Object.keys(payload).length === 0) {
      return {
        status: STATUS.ERROR,
        error: "At least one field (name or context) must be provided",
      };
    }

    const response = await axios.put(
      `${process.env.REACT_APP_API_URL}/${projectId}/modules/${id}`,
      payload,
      {
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error updating module:", error);
    return extractErrorInfo(error);
  }
}

export async function deleteModule({ id } = {}) {
  try {
    const projectId = getProjectId();
    
    if (!projectId) {
      return {
        status: STATUS.ERROR,
        error: "Project ID is required",
      };
    }

    if (!id) {
      return {
        status: STATUS.ERROR,
        error: "Module ID is required",
      };
    }

    const response = await axios.delete(
      `${process.env.REACT_APP_API_URL}/${projectId}/modules/${id}`,
      {
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error deleting module:", error);
    return extractErrorInfo(error);
  }
}

export async function getLevel1Modules() {
  try {
    const projectId = getProjectId();
    
    if (!projectId) {
      return {
        status: STATUS.ERROR,
        error: "Project ID is required",
      };
    }

    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/${projectId}/modules/getLevel1Modules`,
      {
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || [],
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error fetching level 1 modules:", error);
    return extractErrorInfo(error);
  }
}

export async function getChildModules({ module_id } = {}) {
  try {
    const projectId = getProjectId();
    
    if (!projectId) {
      return {
        status: STATUS.ERROR,
        error: "Project ID is required",
      };
    }

    const params = {};
    if (module_id) {
      params.module_id = module_id;
    }

    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/${projectId}/modules/getChildModules`,
      {
        params,
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || [],
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error fetching child modules:", error);
    return extractErrorInfo(error);
  }
}

export async function getModuleNames({ ids } = {}) {
  try {
    const projectId = getProjectId();
    
    if (!projectId) {
      return {
        status: STATUS.ERROR,
        error: "Project ID is required",
      };
    }

    if (!ids || !Array.isArray(ids) || ids.length === 0) {
      return {
        status: STATUS.ERROR,
        error: "At least one module ID is required",
      };
    }

    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/${projectId}/modules/getName`,
      {
        params: { ids },
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || [],
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error fetching module names:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Reorder sibling modules under a parent (null parentId = roots).
 * @param {{ parentId?: string|null, orderedIds: string[], signal?: AbortSignal }} params
 */
export async function reorderModules({ parentId = null, orderedIds, signal } = {}) {
  try {
    const projectId = getProjectId();

    if (!projectId) {
      return {
        status: STATUS.ERROR,
        error: "Project ID is required",
      };
    }

    if (!Array.isArray(orderedIds) || orderedIds.length === 0) {
      return {
        status: STATUS.ERROR,
        error: "orderedIds must be a non-empty array",
      };
    }

    const response = await axios.put(
      `${process.env.REACT_APP_API_URL}/${projectId}/modules/reorder`,
      {
        parentId: parentId ?? null,
        orderedIds,
      },
      {
        withCredentials: true,
        signal,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED' || error?.name === 'AbortError') {
      return {
        status: STATUS.ERROR,
        error: 'Request aborted',
        aborted: true,
      };
    }
    console.error("Error reordering modules:", error);
    return extractErrorInfo(error);
  }
}

